import { Field, Formik, Form } from "formik";
import { useStoreAnalyze } from "hooks/analyzes";
import { useState } from "react";
import moment from "moment";
import {
  Button,
  CalendarInput,
  FullscreenModal,
  Input,
  DropFileInput,
  Textarea,
} from "shared";
import { StoreAnalyzeType } from "types/AnalyzeTypes";
import validation from "./validation";
import { UserType } from "types/UsersTypes";
import "./AddAnalyze.scss";
import { isValidPolishNip } from "utils/validation/nipValidation";

interface AddAnalyzeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  planChangeInitiatedAt: Date | undefined;
  user: UserType | undefined;
}

const AddAnalyzeModal: React.FC<AddAnalyzeModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { mutateAsync: storeCompany } = useStoreAnalyze();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    companyName: "",
    status: "pending",
    nip: "",
    deadlineAt: "",
    files: [],
  };

  const onSubmit = async (values: StoreAnalyzeType) => {
    setIsSubmitting(true);
    try {
      await storeCompany(
        {
          ...values,
          deadlineAt: moment(values.deadlineAt).format("YYYY-MM-DD HH:mm"),
          files: values.files || [],
        },
        {
          onSuccess: () => {
            onSuccess && onSuccess();
            onClose();
          },
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMinDate = (): Date => {
    return moment.utc().add(7, "days").toDate();
  };

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <h2 className="fullscreen-modal__header">Zleć analizę spółki</h2>
      <p className="fullscreen-modal__subtitle">
        Podaj nazwę i numer identyfikacji podatkowej spółki, którą chcesz
        przeanalizować.
      </p>
      <Formik
        initialValues={initialValues}
        onSubmit={(values: any) => onSubmit(values)}
        validationSchema={validation().shape({
          nip: (validation() as any).fields.nip
            ? (validation() as any).fields.nip.test(
                "nip-control",
                "Niepoprawny numer NIP",
                (value: string) => {
                  const v = value?.trim();
                  if (!v) return true;
                  return isValidPolishNip(v);
                },
              )
            : undefined,
        })}
        enableReinitialize
      >
        {({ errors, values, handleSubmit }) => (
          <Form className="m-width-100 fullscreen-modal__select-margin">
            <Field
              type="text"
              id="companyName"
              name="companyName"
              label={"Nazwa spółki"}
              as={Input}
              maxLength={100}
            />
            <Field
              type="number"
              id="nip"
              name="nip"
              label={"Numer identyfikacji podatkowej"}
              as={Input}
              maxLength={11}
            />
            <Field
              type="textarea"
              id="description"
              name="description"
              label={"Opisz sprawę"}
              optional
              component={Textarea}
              maxLength={250}
            />
            <Field
              type="date"
              id="deadlineAt"
              name="deadlineAt"
              label={"Preferowany termin wykonania analizy"}
              component={CalendarInput}
              minDate={getMinDate()}
              errors={errors}
            />
            <p className="fullscreen-modal__text">
              Na wykonanie analizy rynkowej spółki potrzebujemy minimum 7 dni.
            </p>

            <Field
              className="add-analyze__file-input"
              label="Załącz pliki / dokumenty dotyczące tej spółki"
              name="files"
              optional
              multiple={30}
              id="files"
              as={DropFileInput}
              accept=".png, .jpg, .doc, .docx, .pdf"
              description={
                <>
                  Załączenie dodatkowych materiałów{" "}
                  <span className="bold">(maksymalnie 30 plików)</span> może
                  znacząco zwiększyć precyzję i użyteczność przygotowywanej
                  analizy.
                </>
              }
            />

            <div className="fullscreen-modal__buttons">
              <Button
                label="Anuluj"
                onClick={onClose}
                className="button--stroke button--rounded"
              />
              <Button
                label={"Potwierdź"}
                type="submit"
                onClick={() => null}
                className=" button--rounded button--black"
                isLoading={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    </FullscreenModal>
  );
};

export default AddAnalyzeModal;
